import { ChevronDown, LogIn } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { resolveApartmentNavHref } from '@features/livability/utils/apartmentNav'
import { cn } from '@/lib/utils'
import logo from '@/assets/logo-short.svg'

import { NavItem, NavUser, settingsNavItem } from './navConfig'

function isActive(pathname: string, key: string) {
  if (key === '/') return pathname === '/'
  return pathname === key || pathname.startsWith(key + '/')
}

function NavLink({
  item,
  active,
  onNavigate
}: {
  item: NavItem
  active: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation('layout')
  const label = t(`navigation.${item.labelKey}`)
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const to = resolveApartmentNavHref(item.key, searchParams, pathname) ?? item.key

  const className = cn(
    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors [&_svg]:size-5 [&_svg]:shrink-0',
    active
      ? 'bg-primary/10 text-primary font-semibold'
      : 'text-ink/70 hover:bg-ink/[0.04] hover:text-ink'
  )

  const Icon = item.icon

  if (item.disabled) {
    const link = (
      <span
        aria-disabled="true"
        className={cn(className, 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-ink/70')}
      >
        <Icon />
        {label}
      </span>
    )
    if (!item.disabledTooltipKey) return link
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="max-w-[240px]">
            {t(`navigation.${item.disabledTooltipKey}`)}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (item.external) {
    return (
      <a
        href={item.key}
        target="_blank"
        rel="noreferrer"
        className={className}
        onClick={onNavigate}
      >
        <Icon />
        {label}
      </a>
    )
  }

  return (
    <Link to={to} className={className} onClick={onNavigate}>
      <Icon />
      {label}
    </Link>
  )
}

function NavGroup({
  item,
  onNavigate
}: {
  item: NavItem
  onNavigate?: () => void
}) {
  const { t } = useTranslation('layout')
  const { pathname } = useLocation()
  const children = item.children ?? []
  const hasActiveChild = children.some(
    (child) => !child.external && isActive(pathname, child.key)
  )
  const [open, setOpen] = useState(hasActiveChild)

  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild])

  const Icon = item.icon

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex w-full appearance-none items-center gap-3 rounded-xl border-0 bg-transparent px-3 py-2.5 text-[15px] font-medium transition-colors [&_svg]:size-5 [&_svg]:shrink-0',
          hasActiveChild
            ? 'font-semibold text-ink'
            : 'text-ink/70 hover:bg-ink/[0.04] hover:text-ink'
        )}
      >
        <Icon />
        {t(`navigation.${item.labelKey}`)}
        <ChevronDown
          className={cn(
            'ml-auto !size-4 text-ink/50 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="ml-[1.35rem] mt-1 space-y-1 border-l border-ink/[0.08] pl-2.5">
              {children.map((child) => (
                <NavLink
                  key={child.key}
                  item={child}
                  active={!child.external && isActive(pathname, child.key)}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface AppSidebarProps {
  items: NavItem[]
  user?: NavUser
  hideFooter?: boolean
  onNavigate?: () => void
  className?: string
}

export function AppSidebar({
  items,
  user,
  hideFooter,
  onNavigate,
  className
}: AppSidebarProps) {
  const { t } = useTranslation('layout')
  const { pathname } = useLocation()
  const isAuthenticated = !!user

  return (
    <div className={cn('app-chrome flex h-full flex-col bg-white', className)}>
      <Link
        to="/"
        onClick={onNavigate}
        className="flex h-20 items-center px-6"
      >
        <img src={logo} alt="SNA" className="h-10 w-auto" />
      </Link>

      <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-2">
        {items.map((item) =>
          item.children ? (
            <NavGroup key={item.key} item={item} onNavigate={onNavigate} />
          ) : (
            <NavLink
              key={item.key}
              item={item}
              active={!item.external && isActive(pathname, item.key)}
              onNavigate={onNavigate}
            />
          )
        )}
      </nav>

      {!hideFooter && (
        <div className="border-t border-ink/[0.06] px-4 py-3">
          {isAuthenticated ? (
            <NavLink
              item={settingsNavItem}
              active={isActive(pathname, settingsNavItem.key)}
              onNavigate={onNavigate}
            />
          ) : (
            <Link
              to="/login"
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-ink/70 transition-colors hover:bg-ink/[0.04] hover:text-ink [&_svg]:size-5 [&_svg]:shrink-0"
            >
              <LogIn />
              {t('navigation.login')}
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
